import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { BIZY_DEEP_LINK_TRIGGER, useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { useBizyPeopleEntries } from './use-bizy-people-entries';
import { parseBizyParam } from './use-bizy-route-sync';

export function parseBizyMomentsDeepLink(search: string): boolean {
	return parseBizyParam(search)?.kind === 'noticed';
}

function releaseDeepLinkTrigger() {
	setTimeout(() => {
		if (useBizySidePanelStore.getState().lastTrigger === BIZY_DEEP_LINK_TRIGGER) {
			useBizySidePanelStore.setState({ lastTrigger: 'auto' });
		}
	}, 0);
}

/**
 * Async companion to `useBizyRouteSync` for bare `?bizy=noticed`.
 *
 * Route sync owns the steady-state `?bizy=` param and opens the panel for
 * `noticed`; this hook waits for the people list, then selects the first
 * People Browser card (same as clicking a sidebar row). Used by the moment
 * email "More moments" CTA. Does not strip `bizy` — once a person is viewed,
 * route sync upgrades the URL to `noticed.<slug>`.
 *
 * Mount after `useBizyRouteSync` and before `useBizyDeepLink` so a later
 * `showChat()` cannot clear the selected person.
 */
export function useBizyMomentsDeepLink() {
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	const { search } = useLocation();
	const shouldOpen = isBizyEnabledForUser && parseBizyMomentsDeepLink(search);
	const { entries, isLoading } = useBizyPeopleEntries(shouldOpen);
	const consumedRef = useRef(false);

	useEffect(() => {
		// Bare `noticed` is steady-state (not stripped). Clear consume when it
		// leaves so a later return to the same URL can select the first card again.
		if (!shouldOpen) {
			consumedRef.current = false;
			return;
		}
		if (consumedRef.current || isLoading) return;

		let cancelled = false;

		// Defer person selection so store→route can finish its open transition
		// first. Selecting in the same turn updates prevPersonIdRef during that
		// early return and never pushes `noticed.<slug>`.
		const timer = window.setTimeout(() => {
			if (cancelled || consumedRef.current) return;
			consumedRef.current = true;

			const store = useBizySidePanelStore.getState();
			// Route sync opens for `?bizy=noticed` on member web; admin has no
			// route sync, so open here when still closed.
			if (!store.isOpen) store.open(BIZY_DEEP_LINK_TRIGGER);

			const first = entries[0];
			if (first?.person?.id && first.observedMomentId) {
				store.viewPerson({
					id: first.person.id,
					name: first.person.name ?? '',
					observedMomentId: first.observedMomentId,
				});
			} else {
				store.showChat();
			}

			releaseDeepLinkTrigger();
		}, 0);

		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [shouldOpen, isLoading, entries]);
}
